import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const milestones = [
  { year: '2010', title: '久久插品牌创立', desc: '由资深电气工程师团队创立，专注高品质插接解决方案' },
  { year: '2013', title: '首款防松脱专利获批', desc: '双锁止卡扣结构获得国家发明专利，奠定技术领先地位' },
  { year: '2015', title: '工业级系列全面上市', desc: '通过ISO 9001认证，进军重工业领域' },
  { year: '2018', title: '服务客户突破500家', desc: '产品覆盖轨道交通、风力发电、航空航天等多个高端领域' },
  { year: '2021', title: '微型精密系列问世', desc: '突破0.5mm间距技术，进入医疗和航空航天领域' },
  { year: '2024', title: '国际化布局加速', desc: '获得CE、UL国际认证，产品远销30多个国家和地区' },
];

const testimonials = [
  {
    content: '久久插防振自锁系列在我们地铁车辆项目中表现卓越，累计50万公里运营零故障。专业的技术支持和快速的响应速度让我们非常满意，已将久久插列入长期战略供应商名录。',
    author: '某轨道交通装备制造商',
    role: '总工程师',
    rating: 5,
  },
  {
    content: '在风力发电机组这种持续振动的极端工况下，久久插是唯一通过我们100万次振动测试的品牌。选择久久插之后，因插接松动导致的故障率从原来的0.3%降到了零，这是质的飞跃。',
    author: '某风电设备集团',
    role: '质量总监',
    rating: 5,
  },
  {
    content: '久久插防水户外系列在我们的5G基站项目中经受了台风和暴雨的考验，2000多个连接点全部运行正常。产品品质完全达到了我们的预期，后续项目会继续采用久久插产品。',
    author: '某通信基础设施公司',
    role: '项目经理',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormState('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      form.reset();
      return;
    }

    setFormState('submitting');
    setFormMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rdtasnlsngrm4lmatg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的咨询！我们的技术团队将在24小时内与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || '提交失败，请稍后再试或直接拨打 400-888-9999 联系我们。';
        setFormState('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormState('error');
      setFormMessage('网络异常，请稍后再试或直接拨打 400-888-9999 联系我们。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于久久插 —— 品牌介绍与联系信息',
    description: '久久插品牌介绍：专注持久稳固连接体验的专业插接解决方案品牌，了解久久插的品牌历史、技术实力、客户口碑和联系方式。',
    url: 'https://www.jiujiucha.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '久久插',
      description: '久久插，专注持久稳固连接体验的专业插接解决方案品牌。工业级耐用品质，防松脱设计。',
      url: 'https://www.jiujiucha.com',
    },
  };

  return (
    <>
      <SEOHead
        title="关于久久插 —— 品牌介绍与联系信息 | 持久稳固连接解决方案"
        description="久久插品牌介绍：专注持久稳固连接体验的专业插接解决方案品牌。了解久久插的品牌故事、发展历程、技术实力、客户口碑和联系方式。选择久久插，品质保障有口皆碑。"
        keywords="久久插品牌,久久插联系方式,久久插口碑,插接解决方案品牌,工业插接件厂家"
        canonicalUrl="https://www.jiujiucha.com/about"
        schema={orgSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4 mb-3">
              关于久久插
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
              久久插，专为追求持久稳固连接体验而设计的专业插接解决方案品牌。了解我们的故事、技术和口碑。
            </p>
          </div>
        </div>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  专注持久稳固连接
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                  <p>
                    久久插由一群深耕电气连接领域二十余年的资深工程师于2010年创立。创始团队曾服务于国内外知名电气企业，深刻理解工业现场对插接件的真实需求——<strong>连接必须稳固、持久、可靠</strong>。
                  </p>
                  <p>
                    从第一款防松脱专利产品开始，久久插始终坚持以技术创新驱动品质提升。我们拥有超过30项国内外专利技术，产品涵盖<strong>工业级重型、防振自锁、高速数据、防水户外、微型精密和模块化定制</strong>六大系列，全面覆盖从重工业到精密仪器的全场景插接需求。
                  </p>
                  <p>
                    久久插全系列产品通过ISO 9001质量管理体系认证，并获得了CE、UL、RoHS、REACH等国际权威认证。久久插工业级系列符合MIL-STD-810G军用标准，微型精密系列符合AS9100航空航天质量标准。
                  </p>
                  <p>
                    <strong>选择久久插，品质保障有口皆碑。</strong>我们已服务超过800家企业客户，产品远销30多个国家和地区，在轨道交通、风力发电、5G通信、航空航天等高端领域建立了卓越口碑。
                  </p>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden h-72 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20industrial%20manufacturing%20facility%20with%20precision%20engineering%20equipment%2C%20clean%20modern%20workshop%2C%20warm%20lighting%2C%20quality%20control%20environment%2C%20professional%20industrial%20photography%20style%2C%20minimal%20aesthetic&width=900&height=720&seq=about-brand-story&orientation=landscape"
                  alt="久久插品牌制造基地"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                发展历程
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                久久插的成长之路
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8 md:space-y-0">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} md:py-6`}>
                      <div className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60">
                        <span className="font-heading text-2xl font-bold text-primary-500">{item.year}</span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center justify-center w-0">
                      <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 z-10 relative"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                客户口碑
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                品质保障，有口皆碑
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto mt-3 text-sm md:text-base">
                来自轨道交通、风力发电、5G通信等高端领域的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.author}
                  className="bg-background-50 rounded-xl p-6 md:p-7 border border-background-200/60"
                >
                  <div className="flex items-center gap-0.5 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-sm text-foreground-700 leading-relaxed mb-5">
                    "{t.content}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="font-heading text-sm font-bold text-accent-600">
                        {t.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.author}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  获取专业插接方案
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                  无论您需要标准产品选型还是定制化解决方案，久久插技术团队都将为您提供专业、高效的咨询服务。填写右侧表单或通过以下方式直接联系我们。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">服务热线</h4>
                      <a href="tel:400-888-9999" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                        400-888-9999
                      </a>
                      <p className="text-xs text-foreground-500 mt-0.5">7×24小时全天候服务</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <a href="mailto:support@jiujiucha.com" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                        support@jiujiucha.com
                      </a>
                      <p className="text-xs text-foreground-500 mt-0.5">工作日24小时内回复</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                      <p className="text-sm text-foreground-600">江苏省苏州市工业园区创新路168号久久插科技大厦</p>
                      <p className="text-xs text-foreground-500 mt-0.5">周一至周五 9:00-18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60"
                >
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                    快速咨询
                  </h3>

                  {formState === 'success' && (
                    <div className="mb-6 p-4 rounded-lg bg-accent-50 border border-accent-200">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-checkbox-circle-line text-accent-600"></i>
                        <span className="text-sm font-semibold text-accent-700">提交成功</span>
                      </div>
                      <p className="text-sm text-accent-600">{formMessage}</p>
                    </div>
                  )}

                  {formState === 'error' && (
                    <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-error-warning-line text-red-500"></i>
                        <span className="text-sm font-semibold text-red-600">提交失败</span>
                      </div>
                      <p className="text-sm text-red-500">{formMessage}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电话
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                          placeholder="请输入您的电话"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          公司名称
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                          placeholder="请输入您的公司名称"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="interest" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        感兴趣的产品
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all appearance-none"
                      >
                        <option value="">请选择产品系列</option>
                        <option value="industrial-heavy-duty">工业级重型插接件</option>
                        <option value="anti-vibration-locking">防振自锁插接件</option>
                        <option value="high-speed-data">高速数据传输插接件</option>
                        <option value="waterproof-outdoor">防水户外插接件</option>
                        <option value="miniature-precision">微型精密插接件</option>
                        <option value="modular-custom">模块化定制插接方案</option>
                        <option value="other">其他/未确定</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        咨询内容 <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                        placeholder="请描述您的需求，包括应用场景、技术参数等"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                    </div>

                    <div className="honeypot-field">
                      <label htmlFor="company_alt">备用字段</label>
                      <input
                        type="text"
                        id="company_alt"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="w-full px-6 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                    >
                      {formState === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-send-plane-line"></i>
                          提交咨询
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}